import { Flex, Text, Spacer } from '@chakra-ui/react';
import PeCompanyEstimateAction from 'enums/modules/pe/peCompanyEstimateAction';
import moment from 'moment';
import AppCard from 'presentation/components/shared/card/AppCard';
import GridHeading from 'presentation/components/shared/grid/GridHeading';
import { Color } from 'presentation/theme/colorScheme';
import { FontSize } from 'presentation/theme/fontSizes';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import SliderValue from 'presentation/components/shared/SliderValue';

interface Props {
  data: PeCompanyEstimateFromAnalytics;
}

const PeCompanyEstimateFromAnalyticsSection: FC<Props> = ({ data }) => {
  const { t } = useTranslation();

  const gapBetweenSliders = 20;
  const sliderWidth = `calc((100% - ${gapBetweenSliders}px) / 2)`;

  const actionText: Record<PeCompanyEstimateAction, string> = {
    [PeCompanyEstimateAction.Buy]: t('peEstimateActionBuy'),
    [PeCompanyEstimateAction.Hold]: t('peEstimateActionHold'),
    [PeCompanyEstimateAction.Sell]: t('peEstimateActionSell'),
  };

  const actionTextColor: Record<PeCompanyEstimateAction, Color> = {
    [PeCompanyEstimateAction.Buy]: Color.Profit,
    [PeCompanyEstimateAction.Hold]: Color.Shade500,
    [PeCompanyEstimateAction.Sell]: Color.Loss,
  };

  return (
    <>
      <GridHeading>{t('peCompanyEstimateFromAnalytics')}</GridHeading>
      <AppCard p="20px">
        <Flex flexWrap="wrap" justifyContent="space-between" mb="15px">
          {typeof data.product === 'number' && (
            <SliderValue
              w={sliderWidth}
              label={t('peEstimateProduct')}
              value={data.product}
            />
          )}
          {typeof data.competitors === 'number' && (
            <SliderValue
              w={sliderWidth}
              label={t('peEstimateCompetitors')}
              value={data.competitors}
            />
          )}
          {typeof data.industry === 'number' && (
            <SliderValue
              w={sliderWidth}
              label={t('peEstimateIndustry')}
              value={data.industry}
            />
          )}
          {typeof data.finance === 'number' && (
            <SliderValue
              w={sliderWidth}
              label={t('peEstimateFinances')}
              value={data.finance}
            />
          )}
          {typeof data.investors === 'number' && (
            <SliderValue
              w={sliderWidth}
              label={t('peEstimateInvestors')}
              value={data.investors}
            />
          )}
          {typeof data.team === 'number' && (
            <SliderValue
              w={sliderWidth}
              label={t('peEstimateTeam')}
              value={data.team}
            />
          )}
          {typeof data.partners === 'number' && (
            <SliderValue
              w={sliderWidth}
              label={t('peEstimatePartners')}
              value={data.partners}
            />
          )}
          {typeof data.reputation === 'number' && (
            <SliderValue
              w={sliderWidth}
              label={t('peEstimateReputation')}
              value={data.reputation}
            />
          )}
          {typeof data.clients === 'number' && (
            <SliderValue
              w={sliderWidth}
              label={t('peEstimateClients')}
              value={data.clients}
            />
          )}
        </Flex>
        <Flex alignItems="center">
          <Text mr="40px">{moment(data.publicDate).format('LL')}</Text>
          <Text
              flex="1"
              color={actionTextColor[data.action as PeCompanyEstimateAction]}
              fontWeight={700}
          >
            {data.description}
          </Text>
          <Text
              fontSize={FontSize.lg}
              fontWeight={700}
              color={actionTextColor[data.action as PeCompanyEstimateAction]}
          >
            {actionText[data.action as PeCompanyEstimateAction]}
          </Text>
        </Flex>
        <Spacer h="20px" />
        <Flex alignItems="center">
          <Text mr="90px">{t('lock-up')}</Text>
          <Text
            flex="1"
            color={actionTextColor[data.action as PeCompanyEstimateAction]}
            fontWeight={700}
          >
            { data.lock_up }
          </Text>
        </Flex>
      </AppCard>
    </>
  );
};

export default PeCompanyEstimateFromAnalyticsSection;
